import type {FC} from "react";
import {useCallback, useMemo, useState} from "react";

import type {IToastService} from "../../components/ui/toast";
import ToastAlert from "../components/ToastAlert";
import type {ToastOptions} from "../contexts/ToastContext";
import ToastContext from "../contexts/ToastContext";
import {useLatest} from "../hooks/useLatest";

const ToastProvider: FC<{children: React.ReactElement}> = ({children}) => {
    const [toast, setToast] = useState<IToastService>();

    const toastRef = useLatest(toast);

    const getToast = useCallback(() => {
        if (!toastRef.current) {
            console.warn("Toast is called before it has been set");
            return;
        }
        return toastRef.current;
    }, [toastRef]);

    const show = useCallback(
        (options: ToastOptions) => {
            const toast = getToast();
            toast?.show(options);
        },
        [getToast]
    );

    const close = useCallback(
        (toastId: string) => {
            const toast = getToast();
            toast?.close(toastId);
        },
        [getToast]
    );

    const closeAll = useCallback(() => {
        const toast = getToast();
        toast?.closeAll();
    }, [getToast]);

    const success = useCallback(
        (title: string, duration?: number) =>
            show({
                placement: "bottom",
                render: ({id}) => {
                    return <ToastAlert status="success" title={title} uniqueToastId={id} />;
                },
                duration
            }),
        [show]
    );
    const info = useCallback(
        (title: string) =>
            show({
                placement: "bottom",
                render: ({id}) => {
                    return <ToastAlert status="info" title={title} uniqueToastId={id} />;
                }
            }),
        [show]
    );
    const warning = useCallback(
        (title: string) =>
            show({
                placement: "bottom",
                render: ({id}) => {
                    return <ToastAlert status="warning" title={title} uniqueToastId={id} />;
                }
            }),
        [show]
    );
    const error = useCallback(
        (title: string) =>
            show({
                placement: "bottom",
                render: ({id}) => {
                    return <ToastAlert status="error" title={title} uniqueToastId={id} />;
                }
            }),
        [show]
    );

    return (
        <ToastContext.Provider
            value={useMemo(
                () => ({
                    toast: {
                        show,
                        close,
                        closeAll,
                        success,
                        info,
                        warning,
                        error
                    },
                    setToast
                }),
                [show, close, closeAll, success, info, warning, error]
            )}
        >
            {children}
        </ToastContext.Provider>
    );
};

export default ToastProvider;
